/**
 * This file was copied from the Main repo.
 */

import { has, keyBy, groupBy, mapValues } from "lodash";
import { Wallet, CurrencyCode, Scalars } from "#graphql/supergraphTypes";
import { WalletMonies } from "../Money/types";

type Money = Scalars["Money"]["output"];
export const DEFAULT_PRECISION = 2;
export const DEFAULT_EXCHANGE_RATE = 1;

export const hasCurrency = (wallet: WalletMonies, currency: CurrencyCode): boolean => {
  return (
    !!wallet &&
    wallet.monies.some((walletCurrency: Money) => walletCurrency.currency === currency)
  );
};
export const sumMoneyFromWallet = (
  ...args: { amount: number; currency: CurrencyCode; precision: number }[]
) => {
  return mapValues(
    groupBy(args, (arg) => arg.currency.toLowerCase()),
    (monies) => {
      return monies.reduce((acc, money) => {
        if (!acc.currency) {
          throw Error("Money must have a currency");
        }
        if (acc.precision && acc.precision !== money.precision) {
          throw Error("Precisions must match to be summed");
        }

        return { ...money, amount: acc.amount + money.amount };
      });
    },
  );
};

export const getMoneyFromWallet = (
  wallet: WalletMonies | null | undefined,
  currency: CurrencyCode,
): Money => {
  if (!wallet) {
    return { amount: 0, currency, precision: DEFAULT_PRECISION };
  }
  const matchedCurrency = wallet.monies.find((walletCurrency: Money) => {
    return walletCurrency.currency === currency;
  });

  return matchedCurrency === undefined
    ? { amount: 0, currency, precision: DEFAULT_PRECISION }
    : matchedCurrency;
};

export const getMoneyFromWalletForExtensionField = (
  wallet: Wallet | null | undefined,
  currency: CurrencyCode,
): Money => {
  if (!wallet) {
    return { amount: 0, currency, precision: DEFAULT_PRECISION };
  }
  const money = wallet.monies[0];
  // since extension field doesn't support other currency types for now, we just replace the currency with the one provided
  // so it would have the correct currency symbol to display
  return money !== undefined
    ? { ...money, currency }
    : { amount: 0, currency, precision: DEFAULT_PRECISION };
};

export const getMoneyFromWalletWithOptionalNull = (
  wallet: Wallet | null | undefined,
  currency: CurrencyCode,
  returnNull = false,
): Money | null => {
  if (!wallet) {
    return returnNull ? null : { amount: 0, currency, precision: DEFAULT_PRECISION };
  }
  const matchedCurrency = wallet.monies.find((walletCurrency: Money) => {
    return walletCurrency.currency === currency;
  });

  if (matchedCurrency === undefined) {
    return returnNull ? null : { amount: 0, currency, precision: DEFAULT_PRECISION };
  }

  return matchedCurrency;
};

// operation function such as addition or subtraction with two amounts
type Operation = (val1: number, val2: number) => number;

/**
 * Helper method to add 2 monies array together.
 * @param monies1 - First money list to add
 * @param monies2  - Second money list to add
 * @param operation - Function to perform an operation on a money from monies 1 & monies 2
 * @param ignoreMissingCurrencies - whether to ignore operation if currency does not exist in either monies
 * Sample: monies1: [USD, JPY], monies2: [USD, AUD]
 * ignoreMissingCurrencies = true -> [USD]
 * ignoreMissingCurrencies = false -> [USD, JPY, AUD]
 */
const operateMonies = (
  monies1: Money[] | null,
  monies2: Money[] | null,
  operation: Operation,
  ignoreMissingCurrencies: boolean,
): Money[] => {
  if (monies1 === null && monies2 === null) {
    throw new Error("Cannot add two null values");
  }
  if (monies1 === null) {
    return monies2 as Money[];
  }
  if (monies2 === null) {
    return monies1;
  }
  // creates map of CurrencyCode -> Money out of monies1 list
  const codeToMoney = keyBy(monies1, "currency");
  // iterate through other moneys list and run operation function (addition | subtraction)
  // on the matching currency amounts
  monies2.forEach((money2: Money) => {
    const money2Currency = money2.currency;
    // default to 0 amount and monies2's currency/precision values if currency did not
    // exist in monies1
    const { amount, currency, precision } = codeToMoney[money2Currency] || {
      amount: 0,
      currency: money2Currency,
      precision: money2.precision,
    };
    codeToMoney[money2Currency] = {
      amount: operation(amount, money2.amount),
      currency,
      precision,
    };
  });
  if (ignoreMissingCurrencies) {
    const money1Codes = keyBy(monies1, "currency");
    const money2Codes = keyBy(monies2, "currency");
    return Object.keys(codeToMoney)
      .filter((key) => has(money1Codes, key) && has(money2Codes, key))
      .map((key) => codeToMoney[key]);
  }
  return Object.keys(codeToMoney).map((key) => codeToMoney[key]);
};

export const addMoney = (
  monies1: Money[] | null,
  monies2: Money[] | null,
  ignoreMissingCurrencies = false,
): Money[] => {
  return operateMonies(
    monies1,
    monies2,
    (val1: number, val2: number) => val1 + val2,
    ignoreMissingCurrencies,
  );
};

export const subtractMoney = (
  monies1: Money[],
  monies2: Money[],
  ignoreMissingCurrencies = false,
): Money[] => {
  return operateMonies(
    monies1,
    monies2,
    (val1: number, val2: number) => val1 - val2,
    ignoreMissingCurrencies,
  );
};

/**
 * Convenience methods to add/subtract wallet monies. This will return a new Wallet with the individual monies
 * sum/subtracted up. If it is required that the currency exist in both wallets, ignoreMissingCurrencies must be true.
 * @param wallet1 Wallet 1 with monies to add
 * @param wallet2 Wallet 2 with monies to add
 * @param ignoreMissingCurrencies - whether to ignore missing currencies from either list when performing operation
 */
export const addWalletMonies = (
  wallet1: WalletMonies,
  wallet2: WalletMonies,
  ignoreMissingCurrencies = false,
): WalletMonies => {
  return {
    monies: addMoney(wallet1.monies, wallet2.monies, ignoreMissingCurrencies),
  };
};

export const subtractWalletMonies = (
  wallet1: WalletMonies,
  wallet2: WalletMonies,
  ignoreMissingCurrencies = false,
): WalletMonies => {
  return {
    monies: subtractMoney(wallet1.monies, wallet2.monies, ignoreMissingCurrencies),
  };
};
